import { getDatabase, onValue, ref } from "@firebase/database";
import { notEmpty } from "@product/scmp-sdk";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getFirebaseApp } from "~/lib/firebase";

import type { EntityIdUuidMapEntry } from "./types";

type LiveContentSnapshot = {
  latestLiveContent: string; // the entity uuid
  updatedDate: number; // timestamp in ms
};

export const useLiveContentSubscription = (
  entityUuid: string,
  entityIdUuidMap: Record<string, EntityIdUuidMapEntry>,
) => {
  const firebaseApp = useMemo(() => getFirebaseApp(), []);
  const database = useMemo(() => getDatabase(firebaseApp), [firebaseApp]);
  const contentStatusReference = useMemo(
    () => ref(database, `content_status/${entityUuid}`),
    [database, entityUuid],
  );

  // the random string used to trigger the live content loader
  const [randomString, setRandomString] = useState(Date.now().toString());

  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [newUpdateCount, setNewUpdateCount] = useState(0);
  const [shouldEnable, setShouldEnable] = useState(false);

  useEffect(() => {
    if (!shouldEnable) return;

    const unsubscribe = onValue(
      contentStatusReference,
      data => {
        const snapshot = data.val() as LiveContentSnapshot;

        if (lastUpdate >= snapshot.updatedDate) return;
        // always stick to the latest update
        setLastUpdate(snapshot.updatedDate);
        setRandomString(Date.now().toString());

        const [firstEntry, ...restEntries] = Object.values(entityIdUuidMap).sort(
          (a, b) => b.customTime - a.customTime,
        );
        // when entity uuid from update is the first item of existing live contents
        // that means the live contents unchanged but the live article updated
        if (firstEntry.entityUuid === snapshot.latestLiveContent) return;

        // when entity uuid from update is not the first item but exists in existing live contents
        // probably some item(s) got deleted
        if (restEntries.some(entry => entry.entityUuid === snapshot.latestLiveContent)) return;

        setNewUpdateCount(current => current + 1);
      },
      error => {
        console.error(error);
      },
    );

    return unsubscribe;
  }, [contentStatusReference, entityIdUuidMap, lastUpdate, shouldEnable]);

  return {
    newUpdateCount,
    randomString,
    setNewUpdateCount,
    setShouldEnable,
  };
};

export const useDetectComponentRender = () => {
  const [rendered, setRendered] = useState(false);
  const captureElement = useCallback((element?: HTMLElement | null) => {
    if (!element) return;
    setRendered(true);
  }, []);
  return { captureElement, rendered };
};

export const useDeepLinkLocator = (entityIdUuidMap: Record<string, EntityIdUuidMapEntry>) => {
  const { replace } = useRouter();
  const [entityUuid, setEntityUuid] = useState<string>();
  const [position, setPosition] = useState(0);

  const reset = useCallback(() => {
    setEntityUuid(undefined);
    setPosition(0);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || Object.keys(entityIdUuidMap).length === 0) return;

    const url = new URL(window.location.href);
    if (!notEmpty(url.hash) || !url.hash.startsWith("#live-")) return;

    const entityId = url.hash.replace("#live-", "");
    const entityUuid = entityIdUuidMap?.[entityId]?.entityUuid;
    url.hash = "";

    if (!notEmpty(entityUuid)) return;
    void replace(url, url.toString(), { scroll: false, shallow: true });

    const position = Object.values(entityIdUuidMap)
      .sort((a, b) => b.customTime - a.customTime)
      .findIndex(item => item.entityUuid === entityUuid);

    setEntityUuid(entityUuid);
    setPosition(Math.max(position, 0));
  }, [entityIdUuidMap, replace]);

  return { entityUuid, position, reset };
};
